/**CORE LIBRARIES IMPORT */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/**COMPONENTS / HOOKS IMPORT */
import PartnerLockup from "./PartnerLockup";
import TurkishEmblem from "./TurkishEmblem";
import EventDrinks from "./EventDrinks";
import useInViewOnce from "../hooks/useInViewOnce";
import useEventAvailability, { bookingPathFor } from "../hooks/useEventAvailability";
import { useEventContent } from "../hooks/useContent";

/**DATA IMPORT */
import { formatEventDate, eventDateParts, formatPrice } from "../data/events";
import { TBC } from "../data/site";

const MotionLink = motion.create(Link);

const ease = [0.22, 1, 0.36, 1];
const item = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease } } };

const FeaturedEvent = ({ event: staticEvent }) => {
  const event = useEventContent(staticEvent);
  const [h2Ref, inView] = useInViewOnce(0.5);
  const { availability } = useEventAvailability(event);
  if (!event) return null;

  const { day, month, year } = eventDateParts(event.date);
  const weekday = formatEventDate(event.date, { day: undefined, month: undefined, year: undefined });
  const isRepublicDay = event.theme === "turkish-republic-day";

  return (
    <section className="featured-event" aria-labelledby="featured-event-title">
      <h2 ref={h2Ref} className={`section-heading ${inView ? "in-view" : ""}`}>
        Featured Event
      </h2>

      <motion.article
        className={`event-card ${isRepublicDay ? "event-card-republic" : ""}`}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, ease }}
      >
        {isRepublicDay && <TurkishEmblem className="event-card-emblem" />}

        {/* Date badge */}
        <div className="event-date-badge" aria-hidden="true">
          <span className="event-date-day outline">{day}</span>
          <span className="event-date-month">{month}</span>
          <span className="event-date-year">{year}</span>
          <span className="event-date-weekday">{weekday}</span>
        </div>

        {/* Details */}
        <motion.div
          className="event-card-body"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } } }}
        >
          {event.partners && (
            <motion.div variants={item}>
              <PartnerLockup partners={event.partners} label={event.presentedBy} />
            </motion.div>
          )}

          <motion.h3 id="featured-event-title" className="event-title" variants={item}>
            {event.title}
          </motion.h3>

          <motion.ul className="event-meta" variants={item}>
            <li><span className="event-meta-label">Date</span>{formatEventDate(event.date)}</li>
            <li>
              <span className="event-meta-label">Time</span>
              <span className={event.startTime ? undefined : "is-tbc"}>{event.startTime || TBC}</span>
            </li>
            <li>
              <span className="event-meta-label">Price</span>
              <span>
                <strong className="event-price">{formatPrice(event.price)}</strong> {event.price.basis}
                <span className="event-price-note"> · {event.price.note}</span>
              </span>
            </li>
          </motion.ul>

          <motion.div className="event-menu-preview" variants={item}>
            <p className="event-menu-heading">Event menu</p>
            <p className="section-text">
              <strong>Starters:</strong> {event.menu.starters.join(" · ")}
            </p>
            <p className="section-text">
              <strong>Main course:</strong> {event.menu.main.join(" · ")}
            </p>
          </motion.div>

          {event.drinks && (
            <motion.div variants={item}>
              <EventDrinks drinks={event.drinks} currency={event.price.currency} compact />
            </motion.div>
          )}

          <motion.div className="event-actions" variants={item}>
            <MotionLink
              to={bookingPathFor(event, availability)}
              className="hero-btn hero-btn-active"
              whileHover={{ y: -6, scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
            >
              Book Now
            </MotionLink>
            <MotionLink
              to={`/events/${event.slug}`}
              className="hero-btn"
              whileHover={{ y: -6, scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
            >
              Event Details
            </MotionLink>
          </motion.div>
        </motion.div>
      </motion.article>
    </section>
  );
};

export default FeaturedEvent;
